import { ui } from "../app/store.js";
import { esc } from "../domain/format.js";
import { request } from "../services/api.js";
import { accountSetupTutorial } from "../components/account-setup-guide.js";
import { captchaSlot, captchaHeaders, resetCaptcha } from "../components/turnstile.js";

let token = null;
let invitation = null;
let screen = "loading";
let installed = false;
let onEnrollment;
const post = (url, body, headers = {}) =>
  request(url, { method: "POST", body: JSON.stringify(body), headers });
const repaint = () =>
  globalThis.dispatchEvent(new CustomEvent("milltech:render"));
const errorMarkup = () =>
  `<p class="auth-error" role="alert">${esc(ui.invitationError || "")}</p>`;
const disabled = () => (ui.invitationBusy ? " disabled" : "");
const safeError = (error) => {
  const message = error.message || "This invitation could not be opened.";
  return token ? message.replaceAll(token, "[redacted]") : message;
};
function progress(current, label) {
  return { current, total: 2, label };
}
export function invitationScreen() {
  if (!ui.authInvitationActive) return null;
  if (screen === "loading")
    return {
      title: "Opening Your Invitation",
      description: "Please wait while we check your invitation.",
      content: '<p role="status" class="muted">Checking Invitation…</p>',
    };
  if (screen === "invalid" || screen === "error")
    return {
      title:
        screen === "invalid"
          ? "Invitation Unavailable"
          : "Could Not Open Invitation",
      description:
        screen === "invalid"
          ? "This invitation is invalid, expired, or already used. Ask your administrator to send a new invitation."
          : "Check your connection and try opening this invitation again.",
      content: `<div class="password-actions">${errorMarkup()}${screen === "error" ? '<button class="pri" type="button" data-invitation-action="retry">Try Again</button>' : ""}<button type="button" data-invitation-action="sign-in">Go To Sign-In</button></div>`,
    };
  if (screen === "accepted")
    return {
      title: "Your Account Is Ready",
      description:
        "Sign in with the password you just created. Follow the authenticator setup prompt, or open Settings → Security → Set Up Authenticator.",
      content: `<div class="password-actions">${errorMarkup()}<button class="pri" type="button" data-invitation-action="sign-in">Go To Sign-In</button></div>`,
    };
  if (screen === "review")
    return {
      title: "You’re Invited To Milltech",
      description:
        "Confirm the account your administrator prepared for you, then choose your own password.",
      content: `${accountSetupTutorial()}<div class="auth-enrollment"><label>Your Name<input value="${esc(invitation.name)}" autocomplete="name" readonly aria-readonly="true"></label><label>Work Email<input type="email" value="${esc(invitation.email)}" autocomplete="username" data-case="preserve" readonly aria-readonly="true"></label>${errorMarkup()}<button class="pri" type="button" data-invitation-action="choose-password">Continue</button><button type="button" data-invitation-action="sign-in">Go To Sign-In</button></div>`,
      progress: progress(1, "Your Invitation"),
    };
  const minimum = Math.max(12, Number(invitation.passwordMinimum) || 12);
  return {
    title: "Choose Your Password",
    description: `Use at least ${minimum} characters. After creating your account, follow the setup prompt for Google Authenticator or Microsoft Authenticator, or open Settings → Security to connect the app.`,
    content: `<form id="accept-invitation"><label>New Password<input name="password" type="password" minlength="${minimum}" maxlength="128" autocomplete="new-password" data-case="preserve" required></label><label>Confirm Password<input name="passwordConfirm" type="password" minlength="${minimum}" maxlength="128" autocomplete="new-password" data-case="preserve" required></label>${captchaSlot()}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.invitationBusy ? "Please Wait…" : "Create My Account"}</button><button type="button" data-invitation-action="back"${disabled()}>Back</button></form>`,
    progress: progress(2, "Create Your Account"),
  };
}
export function captureInvitation(value = globalThis.location.href) {
  const url = new URL(value, globalThis.location.origin);
  if (url.pathname !== "/accept-invitation") return false;
  const supplied =
    new URLSearchParams(url.hash.slice(1)).get("token") ||
    url.searchParams.get("token");
  token = supplied && supplied.length <= 1024 ? supplied : null;
  invitation = null;
  screen = token ? "loading" : "invalid";
  url.hash = "";
  url.searchParams.delete("token");
  history.replaceState(null, "", `${url.pathname}${url.search}`);
  ui.user = null;
  ui.setupRequired = false;
  ui.authPendingMfa = false;
  ui.mfaEnrollmentRequired = false;
  ui.authPasswordScreen = null;
  ui.authInvitationActive = true;
  ui.invitationError = "";
  ui.invitationBusy = false;
  return true;
}
export async function inspectInvitation() {
  if (!token) return;
  ui.invitationBusy = true;
  ui.invitationError = "";
  try {
    invitation = await post("/api/invitations/inspect", { token });
    if (!invitation.name || !invitation.email)
      throw new Error("This invitation is invalid or expired.");
    if (invitation.mode) ui.workspaceMode = invitation.mode;
    screen = "review";
  } catch (error) {
    ui.invitationError = safeError(error);
    const invalid = /invalid|expired|already used|not found/i.test(
      error.message,
    );
    screen = invalid ? "invalid" : "error";
    if (invalid) token = null;
  } finally {
    ui.invitationBusy = false;
    repaint();
  }
}
function setBusy(value) {
  ui.invitationBusy = value;
  document
    .querySelector(".auth-card")
    ?.setAttribute("aria-busy", String(value));
  for (const button of document.querySelectorAll(".auth-card button"))
    button.disabled = value;
}
function showError(message) {
  ui.invitationError = message;
  const node = document.querySelector(".auth-error");
  if (node) node.textContent = message;
}
function leaveInvitation() {
  token = null;
  screen = "loading";
  ui.authInvitationActive = false;
  ui.invitationError = "";
  ui.invitationBusy = false;
  if (invitation?.email)
    ui.authDraft = { email: invitation.email, name: invitation.name };
  invitation = null;
  history.replaceState(null, "", "/");
  repaint();
}
async function acceptInvitation(form) {
  if (ui.invitationBusy || !token || !invitation) return;
  const data = Object.fromEntries(new FormData(form));
  if (data.password !== data.passwordConfirm) {
    showError("Your passwords do not match.");
    return;
  }
  if (data.password.length < Math.max(12, invitation.passwordMinimum || 12)) {
    showError(
      `Use a password of at least ${Math.max(12, invitation.passwordMinimum || 12)} characters.`,
    );
    return;
  }
  showError("");
  setBusy(true);
  try {
    const captcha = captchaHeaders(form);
    await post("/api/invitations/accept", { token, password: data.password });
    token = null;
    screen = "accepted";
    form.reset();
    history.replaceState(null, "", "/");
    ui.authDraft = { email: invitation.email, name: invitation.name };
    const signedIn = await post("/api/auth/sign-in/email", {
      email: invitation.email,
      password: data.password,
    }, captcha);
    if (signedIn.twoFactorRedirect) {
      ui.authInvitationActive = false;
      ui.authPendingMfa = true;
      ui.authUseRecovery = false;
      repaint();
      return;
    }
    const session = await request("/api/session");
    ui.authInvitationActive = false;
    if (session.mode) ui.workspaceMode = session.mode;
    if (session.mfaEnrollmentRequired) {
      onEnrollment(session.securityUser);
      return;
    }
    location.reload();
  } catch (error) {
    const message = safeError(error);
    if (!token) {
      screen = "accepted";
      repaint();
    } else if (/invalid|expired|already used|not found/i.test(error.message)) {
      token = null;
      form.reset();
      screen = "invalid";
      repaint();
    }
    showError(message);
  } finally {
    resetCaptcha(form);
    setBusy(false);
  }
}
export function installInvitationEvents(root = document, options = {}) {
  if (installed) return;
  installed = true;
  onEnrollment = options.onEnrollment;
  root.addEventListener("submit", (event) => {
    if (event.target.id !== "accept-invitation") return;
    event.preventDefault();
    void acceptInvitation(event.target);
  });
  root.addEventListener("click", (event) => {
    const action = event.target.closest?.("[data-invitation-action]")?.dataset
      .invitationAction;
    if (!action || ui.invitationBusy) return;
    if (action === "retry") void inspectInvitation();
    if (action === "sign-in") leaveInvitation();
    if (action === "choose-password" && invitation) {
      screen = "password";
      ui.invitationError = "";
      repaint();
      document.querySelector('[name="password"]')?.focus();
    }
    if (action === "back") {
      screen = "review";
      ui.invitationError = "";
      repaint();
    }
  });
}
